/**
 * Rules of the cast of a directed video that the API and the web app share (fix 2026-10-18, the
 * first real directed video): the story characters that still need a cast picture, and how a style
 * fits the art style of the cast pictures. Pure, no heavy imports.
 */
import type {
  DirectedCastEntry,
  DirectedCastFit,
  DirectedStory,
  DirectedStoryCharacter,
  DirectedStyle,
} from './directedVideoApi';

/** A name in a plain form: lower case, no quotes, one space. Pure. */
export function normalCastName(name: string): string {
  return name
    .toLowerCase()
    .replace(/["'’`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The characters of a story that need a picture and have no cast entry yet. A cast entry covers a
 * character when its label is the name of the character (case and spaces do not count). Pure.
 */
export function storyCharactersWithoutCast(
  story: Pick<DirectedStory, 'characters'> | null,
  cast: readonly Pick<DirectedCastEntry, 'label'>[]
): DirectedStoryCharacter[] {
  if (story === null) {
    return [];
  }
  const labels = new Set(cast.map((entry) => normalCastName(entry.label)).filter((label) => label !== ''));
  return (story.characters ?? []).filter(
    (character) => character.needsPicture && !labels.has(normalCastName(character.name))
  );
}

/** The kind of an art style or a look: a photo, a drawn or animated image, or not known. */
export type DirectedLookKind = 'photo' | 'stylised' | 'unknown';

const STYLISED_WORDS =
  /illustrat|drawn|drawing|line[- ]?art|cartoon|anime|animat|painted|painting|watercolou?r|sketch|comic|vector|cel[- ]shad|3d render|cgi|pixel|claymation|stop[- ]motion|graphic novel|rotoscop|neon outline|hand[- ]drawn|2d\b/i;
const PHOTO_WORDS =
  /\bphoto|photograph|realistic|photoreal|naturalistic|documentary|live[- ]action|\b(?:16|35|65)\s?mm\b|film stock|film grain|kodak|digital cinema|camera footage/i;
const MIXED_WORDS = /mixed[- ]media|collage|composited|live[- ]action and animation|animation over live/i;

/** What kind of image a text names: stylised words win over photo words. Pure. */
export function lookKindOf(text: string): DirectedLookKind {
  if (STYLISED_WORDS.test(text)) {
    return 'stylised';
  }
  return PHOTO_WORDS.test(text) ? 'photo' : 'unknown';
}

/** The art styles of the cast pictures that show a person or an object (a place or a look does not count). Pure. */
export function castArtStyles(cast: readonly DirectedCastEntry[]): string[] {
  return cast
    .filter((entry) => entry.role === 'person' || entry.role === 'object')
    .map((entry) => entry.description?.artStyle.trim() ?? '')
    .filter((artStyle) => artStyle !== '');
}

export interface DirectedStyleCastFit {
  fit: DirectedCastFit;
  /** The one line of the director, or null when the rule decided. */
  reason: string | null;
}

/**
 * How a style fits the art style of the cast. The director's own judgement when the style holds it
 * (`castFit`), else a small rule on the words:
 * - no cast art style, or a style that says mixed media on purpose: good;
 * - a cast with drawn and photo pictures: mixed;
 * - a drawn cast and a photo-real style, or a photo cast and a drawn style: conflict;
 * - else good.
 * Pure.
 */
export function directedStyleCastFit(style: DirectedStyle, cast: readonly DirectedCastEntry[]): DirectedStyleCastFit {
  if (style.castFit !== undefined) {
    const reason = style.castFitReason?.trim() ?? '';
    return { fit: style.castFit, reason: reason === '' ? null : reason };
  }
  const kinds = new Set(
    castArtStyles(cast)
      .map(lookKindOf)
      .filter((kind) => kind !== 'unknown')
  );
  if (kinds.size === 0) {
    return { fit: 'good', reason: null };
  }
  const look = [style.title, style.camera, style.colourAndLight, style.texture].join(' ');
  if (MIXED_WORDS.test(look)) {
    return { fit: 'good', reason: null };
  }
  if (kinds.size > 1) {
    return { fit: 'mixed', reason: null };
  }
  const castKind = [...kinds][0];
  const lookKind = lookKindOf(look);
  if (lookKind !== 'unknown' && lookKind !== castKind) {
    return { fit: 'conflict', reason: null };
  }
  return { fit: 'good', reason: null };
}

/** The reason of the 400 `M24_DIRECTED_STEP` while a cast member is not ready. */
export const DIRECTED_CAST_PENDING_REASON = 'cast_pending';

/**
 * True while a cast member is not ready (owner fix 2026-10-19: "while we AI create cast and wait for
 * the image to generate and then to be read, the next buttons should be locked"): a picture that the
 * image model makes now (`castPicturesPending` with `working`), an upload that goes up now
 * (`uploads`, web only), or an entry whose picture the vision model still reads
 * (`descriptionStatus: 'reading'`). A failed read does not hold the cast: the artist writes the
 * description. The API refuses a director run by the same rule (400 `M24_DIRECTED_STEP`, reason
 * `DIRECTED_CAST_PENDING_REASON`). Pure.
 */
export function directedCastIsPending(
  cast: readonly Pick<DirectedCastEntry, 'descriptionStatus'>[],
  pending: readonly { status: 'working' | 'failed' }[],
  uploads = 0
): boolean {
  return (
    uploads > 0 ||
    pending.some((picture) => picture.status === 'working') ||
    cast.some((entry) => entry.descriptionStatus === 'reading')
  );
}
